/**
 * UnitDecisionsSection
 *
 * Internal offer-workspace component showing the client's persisted per-unit
 * decisions (accepted/rejected/undecided) for a specific finalized version:
 * a summary (counts + accepted value), a searchable/filterable/sortable
 * virtualized table with unit identity, accepted dates, offered price, and
 * reservation state, plus the remove-rejected-units action. Reserving
 * accepted nośniki and freeing rejected ones' holds moved to the merged
 * "Zarezerwuj wstępnie" action in the offer workspace's finalization panel
 * (offer-accept-reservation / "Preliminary reservation action in the offer
 * workspace finalization panel") — this tab offers only the action that
 * removes rejected nośniki from the offer's draft.
 *
 * Traceability: client-offer-decision-persistence / Internal visibility of
 * per-unit client decisions; Manual removal of rejected units from the
 * offer draft.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { offerVersionEndpoints } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { TableSkeleton } from '../../layouts/skeletons';
import { unitDecisionsRequest } from './api/queries';
import { RemoveRejectedUnitsDialog } from './remove-rejected-units-dialog';
import { UnitDecisionsSummaryPanel } from './unit-decisions/summary-panel';
import { UnitDecisionsToolbar } from './unit-decisions/toolbar';
import { UnitDecisionsTable } from './unit-decisions/table';
import {
  computeSummary,
  filterUnits,
  sortUnits,
  type DecisionFilterKey,
  type SortDir,
  type SortKey,
  UNIT_DECISIONS_SORT_KEYS,
} from './unit-decisions/filters';
import { useUnitDecisionTableColumns } from './unit-decisions/table-columns';

// ─── Remove-rejected-units trigger ──────────────────────────────

function RemoveRejectedTrigger({
  canManageRemoval,
  hasRejected,
  onOpen,
}: {
  canManageRemoval: boolean;
  hasRejected: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation('offers');
  if (!canManageRemoval) return null;

  return (
    <div className="flex justify-end">
      <DisabledHintTooltip
        disabled={!hasRejected}
        hint={t('workspace.versions.unitDecisions.remove.disabledHint')}
      >
        <Button
          variant="outline"
          size="sm"
          disabled={!hasRejected}
          onClick={onOpen}
          data-testid="remove-rejected-units-button"
        >
          <Trash2 className="h-4 w-4" />
          {t('workspace.versions.unitDecisions.remove.button')}
        </Button>
      </DisabledHintTooltip>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────

export interface UnitDecisionsSectionProps {
  offerId: string;
  versionId: number;
  /** canEdit && useHasPermission('reservation.manage') — same gate the
   * preliminary-reservation action requires (client-offer-decision-
   * persistence / "Manual removal of rejected units from the offer draft"). */
  canManageRemoval: boolean;
  canViewSupplierDetails?: boolean;
  sortKey?: SortKey;
  sortDir?: SortDir;
  onSortChange?: (sortKey: SortKey, sortDir: SortDir) => void;
}

function resolveTableState(props: UnitDecisionsSectionProps) {
  return {
    canViewSupplierDetails: props.canViewSupplierDetails ?? true,
    sortKey: props.sortKey ?? ('decision' as const),
    sortDir: props.sortDir ?? ('asc' as const),
    onSortChange: props.onSortChange,
  };
}

export function UnitDecisionsSection(props: UnitDecisionsSectionProps) {
  return (
    <QueryBoundary fallback={<TableSkeleton />}>
      <UnitDecisionsContent {...props} />
    </QueryBoundary>
  );
}

function UnitDecisionsContent(props: UnitDecisionsSectionProps) {
  const { offerId, versionId, canManageRemoval } = props;
  const { canViewSupplierDetails, sortKey, sortDir, onSortChange } = resolveTableState(props);
  const { t } = useTranslation('offers');
  const { data: items } = useApiSuspenseQuery(
    offerVersionEndpoints.unitDecisions,
    unitDecisionsRequest(offerId, versionId),
  );

  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState<DecisionFilterKey>('ALL');
  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false);

  const { columns, orderedColumns, visibleColumns, preferences } = useUnitDecisionTableColumns(
    t,
    canViewSupplierDetails,
  );

  function handleSort(key: string) {
    const nextKey = UNIT_DECISIONS_SORT_KEYS.find((candidate) => candidate === key);
    if (!nextKey) return;
    onSortChange?.(nextKey, nextKey === sortKey && sortDir === 'asc' ? 'desc' : 'asc');
  }

  const hasRejected = items.some((item) => item.decision === 'REJECTED');

  if (items.length === 0) {
    // No units at all on this version — nothing to summarize or act on.
    return <div data-testid="unit-decisions-section" onClick={(e) => e.stopPropagation()} />;
  }

  const summary = computeSummary(items);
  const rows = sortUnits(filterUnits(items, filter, query), sortKey, sortDir);

  const sortLabel = columns.find((c) => c.sortKey === sortKey || c.id === sortKey)?.label;

  return (
    <div
      className="space-y-3"
      data-testid="unit-decisions-section"
      onClick={(e) => e.stopPropagation()}
    >
      <RemoveRejectedTrigger
        canManageRemoval={canManageRemoval}
        hasRejected={hasRejected}
        onOpen={() => setRemoveDialogOpen(true)}
      />

      <UnitDecisionsSummaryPanel summary={summary} />

      <UnitDecisionsToolbar
        query={query}
        onQueryChange={setQuery}
        activeFilter={filter}
        onFilterChange={setFilter}
        counts={{
          all: summary.total,
          accepted: summary.accepted,
          rejected: summary.rejected,
          undecided: summary.undecided,
        }}
        shownCount={rows.length}
        columnPickerColumns={orderedColumns}
        hiddenColumnIds={preferences.hiddenColumnIds}
        onToggleColumn={preferences.toggleColumn}
        onMoveColumnUp={preferences.moveColumnUp}
        onMoveColumnDown={preferences.moveColumnDown}
      />

      <UnitDecisionsTable
        rows={rows}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onClearFilters={() => {
          setFilter('ALL');
          setQuery('');
        }}
        columns={visibleColumns}
        onColumnReorder={preferences.reorderColumn}
        onColumnResizeEnd={preferences.setColumnWidth}
      />

      <p className="text-xs text-muted-foreground" data-testid="unit-decisions-sort-label">
        {t('workspace.versions.unitDecisions.table.sortLabel', {
          field: sortLabel ?? sortKey,
          dir: sortDir === 'asc' ? '\u2191' : '\u2193',
        })}
      </p>

      {removeDialogOpen ? (
        <RemoveRejectedUnitsDialog
          offerId={offerId}
          versionId={versionId}
          items={items}
          onClose={() => setRemoveDialogOpen(false)}
        />
      ) : null}
    </div>
  );
}
